import { DiagramSteps } from '../lineView/DiagramSteps'
import type { LineDiagramSpec } from '../lineView/LineDiagram'

/*
 * Rail diagrams of New York's lines that are not one straight path (line
 * view station list). Same authoring rules as `londonLineDiagrams.ts`.
 *
 * Express tracks that skip local stops (the 6 at Parkchester, the 7 between
 * Mets–Willets Point and 74 St, the HBLR past Hoboken) are not branches:
 * the list follows the local stopping pattern on one rail, so every station
 * is listed once and every hop is a real connection.
 *
 * `nycLineDiagrams.bundled.test.ts` checks every table against the NYC map
 * set (`cities/nyc/map-v2`).
 */

const { branch, via, loop, start, end, stops, at, back } = DiagramSteps

export const NYC_LINE_DIAGRAMS: Readonly<Record<string, LineDiagramSpec>> = {
  '5': {
    rails: 2,
    steps: [
      branch('nereid-av'),
      start(0, 'nereid-av'),
      ...stops(0, '233-st', '225-st', '219-st', 'gun-hill-rd-2', 'burke-av', 'allerton-av'),
      ...stops(0, 'pelham-pkwy-2', 'bronx-park-east'),
      branch('eastchester-dyre-av'),
      start(1, 'eastchester-dyre-av'),
      ...stops(1, 'baychester-av', 'gun-hill-rd-5', 'pelham-pkwy-5', 'morris-park'),
      at(0, 'e-180-st', { merge: [1] }),
      ...stops(0, 'west-farms-sq-e-tremont-av', '174-st', 'freeman-st', 'simpson-st'),
      ...stops(0, 'intervale-av', 'prospect-av-2', 'jackson-av', '3-av-149-st'),
      ...stops(0, '149-st-hostos-2', '138-st-grand-concourse', '125-st-4', '86-st-4', '59-st-4'),
      ...stops(0, 'grand-central-42-st-4', '14-st-union-sq-4', 'brooklyn-bridge-city-hall'),
      ...stops(0, 'fulton-st-4', 'wall-st-4', 'bowling-green', 'borough-hall-4', 'nevins-st'),
      ...stops(0, 'atlantic-av-barclays-ctr-2', 'franklin-av-medgar-evers-college'),
      ...stops(0, 'president-st-medgar-evers-college', 'sterling-st', 'winthrop-st'),
      ...stops(0, 'church-av-2', 'beverly-rd', 'newkirk-av-little-haiti'),
      end(0, 'flatbush-av-brooklyn-college'),
    ],
  },
  '6': {
    rails: 1,
    steps: [
      start(0, 'pelham-bay-park'),
      ...stops(0, 'buhre-av', 'middletown-rd', 'westchester-sq-e-tremont-av', 'zerega-av'),
      ...stops(0, 'castle-hill-av', 'parkchester', 'st-lawrence-av', 'morrison-av-soundview'),
      ...stops(0, 'elder-av', 'whitlock-av', 'hunts-point-av', 'longwood-av', 'e-149-st'),
      ...stops(0, 'e-143-st-st-marys-st', 'cypress-av', 'brook-av', '3-av-138-st', '125-st-4'),
      ...stops(0, '116-st-6', '110-st', '103-st-6', '96-st-6', '86-st-4', '77-st-6'),
      ...stops(0, '68-st-hunter-college', '59-st-4', '51-st', 'grand-central-42-st-4', '33-st'),
      ...stops(0, '28-st-6', '23-st-baruch-college', '14-st-union-sq-4', 'astor-pl'),
      ...stops(0, 'bleecker-st', 'spring-st-6', 'canal-st-6'),
      end(0, 'brooklyn-bridge-city-hall'),
    ],
  },
  '7': {
    rails: 1,
    steps: [
      start(0, '34-st-hudson-yards'),
      ...stops(0, 'times-sq-42-st-7', '5-av', 'grand-central-42-st-7', 'vernon-blvd-jackson-av'),
      ...stops(0, 'hunters-point-av', 'court-sq-7', 'queensboro-plaza-7', '33-st-rawson-st'),
      ...stops(0, '40-st-lowery-st', '46-st-bliss-st', '52-st', '61-st-woodside', '69-st'),
      ...stops(0, '74-st-broadway', '82-st-jackson-hts', '90-st-elmhurst-av', 'junction-blvd'),
      ...stops(0, '103-st-corona-plaza', '111-st-7', 'mets-willets-point'),
      end(0, 'flushing-main-st'),
    ],
  },
  A: {
    rails: 2,
    steps: [
      start(0, 'inwood-207-st'),
      ...stops(0, 'dyckman-st-a', '190-st', '181-st-a', '175-st', '168-st', '145-st-a'),
      ...stops(0, '125-st-a', '59-st-columbus-circle-a', '42-st-port-authority-bus-terminal'),
      ...stops(0, '34-st-penn-station-a', '14-st-a', 'w-4-st-wash-sq-a', 'canal-st-a'),
      ...stops(0, 'chambers-st-a', 'fulton-st-a', 'high-st', 'jay-st-metrotech-a'),
      ...stops(0, 'hoyt-schermerhorn-sts', 'nostrand-av-a', 'utica-av', 'broadway-junction-a'),
      ...stops(0, 'euclid-av', 'grant-av', '80-st', '88-st'),
      at(0, 'rockaway-blvd', { split: [1] }),
      branch('ozone-park-lefferts-blvd'),
      ...stops(1, '104-st-a', '111-st-a'),
      end(1, 'ozone-park-lefferts-blvd'),
      via('howard-beach-jfk-airport'),
      ...stops(0, 'aqueduct-racetrack', 'aqueduct-n-conduit-av', 'howard-beach-jfk-airport'),
      at(0, 'broad-channel', { split: [1] }),
      branch('rockaway-park-beach-116-st'),
      ...stops(1, 'beach-90-st', 'beach-98-st', 'beach-105-st'),
      end(1, 'rockaway-park-beach-116-st'),
      branch('far-rockaway-mott-av'),
      ...stops(0, 'beach-67-st', 'beach-60-st', 'beach-44-st', 'beach-36-st', 'beach-25-st'),
      end(0, 'far-rockaway-mott-av'),
    ],
  },
  HBLR: {
    rails: 2,
    steps: [
      start(0, 'tonnelle-avenue-light-rail-station'),
      ...stops(0, 'bergenline-ave', 'port-imperial-hblr-station'),
      ...stops(0, 'lincoln-harbor-light-rail-station', '9th-street-light-rail-station'),
      ...stops(0, '2nd-street-light-rail-station', 'hoboken-terminal-light-rail-station'),
      ...stops(0, 'newport-light-rail-station', 'harsimus-cove-light-rail-station'),
      ...stops(0, 'harborside-light-rail-station', 'exchange-place-light-rail-station'),
      ...stops(0, 'essex-street-light-rail-station', 'marin-boulevard-light-rail-station'),
      ...stops(0, 'jersey-avenue-light-rail-station'),
      at(0, 'liberty-state-park-ride-light-rail-sta', { split: [1] }),
      branch('west-side-avenue-light-rail-station'),
      ...stops(1, 'garfield-avenue-light-rail-station', 'mlk-drive-light-rail-station'),
      end(1, 'west-side-avenue-light-rail-station'),
      branch('8th-street-light-rail-station'),
      ...stops(0, 'richard-street-light-rail-station', 'danforth-avenue-light-rail-station'),
      ...stops(0, '45th-street-light-rail-station', '34th-street-light-rail-station'),
      ...stops(0, '22nd-street-light-rail-station'),
      end(0, '8th-street-light-rail-station'),
    ],
  },
  // AirTrain JFK from Jamaica: out to Federal Circle, then once round the
  // terminal loop.
  '2878': {
    rails: 1,
    steps: [
      start(0, 'jamaica'),
      ...stops(0, 'federal-circle'),
      loop,
      ...stops(0, 'terminal-1', 'terminal-4', 'terminal-5', 'terminal-7', 'terminal-8'),
      back(0, 'federal-circle'),
    ],
  },
  NJT: {
    rails: 2,
    steps: [
      start(0, 'new-york-penn-station'),
      at(0, 'secaucus-upper-level', { split: [1] }),
      branch('newark-airport-railroad-station'),
      ...stops(1, 'newark-penn-station'),
      end(1, 'newark-airport-railroad-station'),
      branch('meadowlands-rail-station'),
      ...stops(0, 'newark-broad-st', 'hoboken-njt', 'secaucus-lower-level'),
      end(0, 'meadowlands-rail-station'),
    ],
  },
}
